import './ListPage.scss';
import { Button, Icon, SimpleNumberCard, Checkbox } from '@design-system/index';
import {
    ArchiveLine,
    ArchiveFilled,
    AddDashed,
    UnarchiveFilled,
    UnarchiveLine,
    WarningLine,
    RoundedArrow
} from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { ColumnDef } from '@tanstack/react-table';
import { LargeIconButton } from '@design-system/components/Buttons/LargeIconButton/LargeIconButton';
import { useListPageVM } from './useListPageVM';
import { Permission } from '@components/Permission/Permission';
import { FlashInfoTable } from '@services/member/flash-info/flash-info.types';

export default function ListPage() {
    const {
        handleToggleArchiveFlashInfo,
        handleToggleArchiveFlashInfos,
        handleToggleFlashInfoActive,
        handleMetadata,
        navigate,
        columnHelper,
        isArchived,
        setIsArchived,
        totalFlashInfos,
        mutationHook,
        isLoading,
        needReload,
        user
    } = useListPageVM();

    const columns = [
        columnHelper.display({
            id: 'select',
            size: 0,
            meta: {
                role: 'admin'
            },
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            )
        }),
        columnHelper.accessor('title', {
            header: 'Titre',
            id: 'title',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{info.getValue()}</>
        }),

        columnHelper.accessor((row) => row.content, {
            header: 'Contenu',
            id: 'content',
            meta: {
                clickable: true
            },
            enableSorting: true,
            cell: (info) => <div dangerouslySetInnerHTML={{ __html: info.getValue() ?? '' }} style={{ whiteSpace: 'pre-line' }} />
        }),
        columnHelper.accessor((row) => row.image, {
            id: 'image',
            header: 'Image',
            meta: {
                clickable: true
            },
            enableSorting: true,
            cell: (info) => <>{<img className="flash-info-image" src={info.getValue()?.viewLink ?? ''} />}</>
        }),
        columnHelper.accessor((row) => row.link, {
            id: 'link',
            header: 'Lien',
            meta: {
                clickable: true
            },
            size: 40,
            enableSorting: true,
            cell: (info) => {
                const link = info.getValue();
                return link ? (
                    <Button
                        variant="icon-hover"
                        color="grey-secondary"
                        icon={<Icon icon={<RoundedArrow />} size={24} />}
                        onClick={() => {
                            window.open(info.getValue(), '_blank');
                        }}
                    />
                ) : null;
            }
        }),
        columnHelper.accessor((row) => row.isActive, {
            id: 'isActive',
            header: 'Actif',
            enableSorting: true,
            size: 40,
            meta: {
                clickable: false
            },
            cell: (info) => (
                <div style={{ display: 'flex', justifyContent: 'start' }}>
                    <Checkbox
                        checked={info.getValue()}
                        onChange={() => {
                            handleToggleFlashInfoActive(info.row.original.id, info.getValue());
                        }}
                    />
                </div>
            )
        }),
        columnHelper.display({
            id: 'archive',
            size: 40,
            header: isArchived ? 'Désarchiver' : 'Archiver',
            meta: {
                role: 'admin',
            },
            cell: (info) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'start' }}>
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />}
                            icon={<Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />}
                            onClick={() => handleToggleArchiveFlashInfo(info.row.original.id, !isArchived)}
                        />
                    </div>
                );
            }
        })
    ] as ColumnDef<FlashInfoTable>[];

    return (
        <div className="members-table-container">
            <div className="members-table-container-header">
                <div className="members-table-container-header-left">
                    <SimpleNumberCard
                        labelForSingle={isArchived ? 'Information archivée' : 'Information'}
                        labelForMultiple={isArchived ? 'Informations archivées' : 'Informations'}
                        value={totalFlashInfos}
                    />
                    <LargeIconButton
                        icon={<AddDashed />}
                        label="Nouvelle information"
                        onClick={() => navigate('/products-management/flash-infos/add')}
                    />
                </div>
                <Permission role="admin">
                    <Button variant="secondary" onClick={() => setIsArchived(!isArchived)}>
                        {isArchived ? 'Actifs' : 'Archivés'}
                    </Button>
                </Permission>
            </div>
            <MutationTable
                key={isArchived ? 'archived' : 'active'}
                columns={columns}
                forceLoading={isLoading}
                selectable={user?.role === 'admin'}
                needReload={needReload}
                mutationHook={mutationHook}
                onRowClick={(row) => navigate(`/products-management/flash-infos/${row.id}/edit`)}
                defaultSorting={{
                    field: 'createdAt',
                    direction: 'desc'
                }}
                headerTitle={isArchived ? 'Liste des informations archivées' : 'Liste des informations'}
                headerIcon={<WarningLine />}
                onMetadata={handleMetadata}
                footer={{
                    actions: [
                        {
                            role: 'admin',
                            label: isArchived ? 'Désarchiver' : 'Archiver',
                            icon: <Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />,
                            hoverIcon: <Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />,
                            onClick: (lines: FlashInfoTable[]) => handleToggleArchiveFlashInfos(lines, !isArchived)
                        }
                    ]
                }}
            />
        </div>
    );
}
